/**
 * Requests: fans waiting for you to approve their follow (ISSUE-1372). Approve
 * makes them a follower; Decline drops the request, and they may ask again,
 * as in v1. Neither asks first: both are the answer to a question the fan was
 * asked, and Decline loses nothing the fan owns. Busy state is per row.
 */
import {
  useFanFollowAccept,
  useFanFollowReject,
  useFanFollowRequestsList,
} from "@sportsmet/profile-sdk/private";
import { Button } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  APPROVE_LABEL,
  DECLINE_LABEL,
  REQUESTS_EMPTY_BODY,
  REQUESTS_EMPTY_HEADLINE,
} from "./followCopy";
import { requestedOnLine } from "./followDates";
import { FOLLOW_LIST_CLASS, FollowListFrame } from "./FollowListFrame";
import {
  canShowMore,
  FOLLOW_LIST_PAGE_SIZE,
  nextFollowListLimit,
} from "./followListPaging";
import { FollowPersonRow } from "./FollowPersonRow";
import { useShowListError } from "./useFollowListQueryErrors";

export const RequestsTab = (): ReactNode => {
  const [limit, setLimit] = useState(FOLLOW_LIST_PAGE_SIZE);
  const list = useFanFollowRequestsList({ limit });
  const accept = useFanFollowAccept();
  const reject = useFanFollowReject();
  useShowListError(list);

  const items = list.data?.data?.items ?? [];
  const busyAccountId = accept.isPending
    ? accept.variables.accountId
    : reject.isPending
      ? reject.variables.accountId
      : null;

  async function answer(
    accountId: string,
    choice: "approve" | "decline",
  ): Promise<void> {
    try {
      const result =
        choice === "approve"
          ? await accept.mutateAsync({ accountId })
          : await reject.mutateAsync({ accountId });
      showApiError(result);
    } catch {
      showOfflineError();
    }
  }

  return (
    <FollowListFrame
      isLoading={list.isPending}
      isEmpty={items.length === 0}
      emptyHeadline={REQUESTS_EMPTY_HEADLINE}
      emptyBody={REQUESTS_EMPTY_BODY}
      canShowMore={canShowMore({
        limit,
        nextCursor: list.data?.data?.nextCursor,
      })}
      onShowMore={() => {
        setLimit(nextFollowListLimit);
      }}
      isShowingMore={list.isFetching}
      testId="follow-requests"
    >
      <ul className={FOLLOW_LIST_CLASS}>
        {items.map((item) => {
          const isBusy = busyAccountId === item.accountId;
          return (
            <FollowPersonRow
              key={item.accountId}
              person={item}
              caption={requestedOnLine(item.requestedAt)}
              testId={`follow-request-${item.accountId}`}
              actions={
                <>
                  <Button
                    size="sm"
                    disabled={isBusy}
                    loading={isBusy && accept.isPending}
                    loadingLabel="Approving"
                    onClick={() => {
                      void answer(item.accountId, "approve");
                    }}
                    data-testid={`follow-request-approve-${item.accountId}`}
                  >
                    {APPROVE_LABEL}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={isBusy}
                    loading={isBusy && reject.isPending}
                    loadingLabel="Declining"
                    onClick={() => {
                      void answer(item.accountId, "decline");
                    }}
                    data-testid={`follow-request-decline-${item.accountId}`}
                  >
                    {DECLINE_LABEL}
                  </Button>
                </>
              }
            />
          );
        })}
      </ul>
    </FollowListFrame>
  );
};
